import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties, ComponentProps, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vault } from '@sorare/core/src/atoms/icons/Vault';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { AnimatedBackgroundBox } from '@sorare/core/src/atoms/ui/AnimatedBackgroundBox';

import { isFixtureLive } from 'lib/so5';

import { MatchesPlayedStat } from '../MatchesPlayedStat';
import type { LeagueSquareLink_so5LeaderboardGroupInterface } from './__generated__/index.graphql';
import { TopContainer } from './ui';

const Root = styled(Horizontal).attrs({ center: true })`
  position: relative;
  padding: var(--custom-padding, calc(5 * var(--unit)) var(--double-unit));
  height: 100%;
`;
const Logo = styled.img`
  object-fit: contain;
`;
const Content = styled(Vertical).attrs({ center: true, grow: true })`
  max-width: 100%;
`;

type Props = {
  so5LeaderboardGroupInterface: LeagueSquareLink_so5LeaderboardGroupInterface;
  logoProps?: {
    width?: number;
    height?: number;
  };
  rewardVariant?: 'top' | 'bottom';
  children?: ReactNode;
  topLeft?: ReactNode;
  topRight?: ReactNode;
  style?: CSSProperties;
} & Pick<
  ComponentProps<typeof AnimatedBackgroundBox>,
  'to' | 'onClick' | 'forceShowBackground' | 'className'
>;

export const LeagueSquareLink = ({
  so5LeaderboardGroupInterface,
  logoProps = {
    width: 180,
    height: 80,
  },
  children,
  topLeft,
  topRight,
  ...rest
}: Props) => {
  const {
    slug,
    displayName,
    primaryColor,
    logoUrl,
    tileBackgroundUrl,
    so5Fixture,
    vaultPointsRequired,
    hasLeaderboards,
  } = so5LeaderboardGroupInterface;

  const isLive = isFixtureLive(so5Fixture);

  return (
    <AnimatedBackgroundBox
      key={slug}
      backgroundDarker={!hasLeaderboards}
      primaryColor={primaryColor}
      backgroundPictureUrl={tileBackgroundUrl}
      {...rest}
      className={classNames(rest.className, 'dark-mode')}
      topRightLabel={
        vaultPointsRequired ? (
          <Horizontal>
            <Vault size={1.5} color="var(--c-yellow-600)" />
            <FormattedMessage
              id="Fixture.Competition.Leaderboard.Tile.vaultLevelReq"
              defaultMessage="Vault Level Req."
            />
          </Horizontal>
        ) : undefined
      }
    >
      <Root>
        <Content>
          <TopContainer>
            {topLeft}
            {topRight || (
              <>
                {!!isLive && (
                  <MatchesPlayedStat
                    so5LeaderboardGroup={so5LeaderboardGroupInterface}
                  />
                )}
              </>
            )}
          </TopContainer>
          <Logo
            src={logoUrl}
            alt={displayName}
            style={{
              opacity: hasLeaderboards ? 1 : 0.2,
            }}
            {...logoProps}
          />
          {children}
        </Content>
      </Root>
    </AnimatedBackgroundBox>
  );
};

LeagueSquareLink.fragments = {
  so5LeaderboardGroupInterface: gql`
    fragment LeagueSquareLink_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      displayName
      logoUrl
      hasLeaderboards
      vaultPointsRequired
      so5Fixture {
        slug
        ...isFixtureLive_so5Fixture
      }
      tileBackgroundUrl
      ...MatchesPlayedStat_so5LeaderboardGroupInterface
    }
    ${MatchesPlayedStat.fragments.so5LeaderboardGroupInterface}
    ${isFixtureLive.fragments.so5Fixture}
  ` as TypedDocumentNode<LeagueSquareLink_so5LeaderboardGroupInterface>,
};
